import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle, ArrowLeft } from "lucide-react";
import { T } from "@/locales/tr";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POLL_MAX = 10;
const POLL_INTERVAL = 2000;

export default function DropshipStatus({ kind = "success" }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", transaction: null });
  const attempts = useRef(0);

  useEffect(() => {
    if (kind !== "success") { setState({ status: "cancelled" }); return; }
    if (!sessionId) { setState({ status: "missing" }); return; }
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const { data } = await axios.get(`${API}/dropship/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", transaction: data.transaction });
          return;
        }
        if (data.status === "expired") { setState({ status: "expired" }); return; }
        attempts.current += 1;
        if (attempts.current >= POLL_MAX) { setState({ status: "timeout", transaction: data.transaction }); return; }
        setTimeout(tick, POLL_INTERVAL);
      } catch {
        attempts.current += 1;
        if (attempts.current >= POLL_MAX) setState({ status: "error" });
        else setTimeout(tick, POLL_INTERVAL);
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId, kind]);

  return (
    <div data-testid="page-dropship-status" className="max-w-2xl mx-auto px-4 sm:px-8 py-16 text-center">
      {state.status === "polling" && (
        <>
          <Loader2 size={48} className="mx-auto animate-spin text-[color:var(--papicoo-gold)]" />
          <h1 className="font-display text-3xl mt-6">{T("On confirme ta commande…")}</h1>
        </>
      )}
      {state.status === "paid" && (
        <>
          <CheckCircle2 size={56} className="mx-auto text-emerald-600" />
          <h1 className="font-display text-3xl mt-6">{T("Commande confirmée 🌹")}</h1>
          <p className="text-[color:var(--papicoo-muted)] mt-3">
            <strong>{T("Jërëjëf.")}</strong> {T("Papicoo a alerté le fournisseur. Tu reçois la confirmation et le suivi par email.")}
          </p>
          {state.transaction?.metadata && (
            <div className="mt-6 papicoo-card p-5 text-left text-sm space-y-1">
              <div>{T("Produit :")} <strong>{state.transaction.metadata.product_name}</strong></div>
              <div>{T("Quantité :")} <strong>{state.transaction.metadata.quantity}</strong></div>
              <div>{T("Livraison :")} <strong>{state.transaction.metadata.shipping_address}, {state.transaction.metadata.shipping_city}, {state.transaction.metadata.shipping_country}</strong></div>
              <div>{T("Total :")} <strong>{(state.transaction.amount_eur || 0).toFixed(2)} €</strong></div>
            </div>
          )}
        </>
      )}
      {state.status === "cancelled" && (
        <>
          <XCircle size={56} className="mx-auto text-amber-600" />
          <h1 className="font-display text-3xl mt-6">{T("Commande annulée")}</h1>
          <p className="text-[color:var(--papicoo-muted)] mt-3">{T("Aucun débit. Retourne au catalogue pour réessayer.")}</p>
        </>
      )}
      {state.status === "expired" && (
        <>
          <XCircle size={56} className="mx-auto text-red-600" />
          <h1 className="font-display text-3xl mt-6">{T("Session expirée")}</h1>
        </>
      )}
      {state.status === "timeout" && (
        <>
          <Loader2 size={48} className="mx-auto text-amber-600" />
          <h1 className="font-display text-3xl mt-6">{T("Vérification en cours")}</h1>
          <p className="text-[color:var(--papicoo-muted)] mt-3">{T("Papicoo confirme par email dès réception du paiement.")}</p>
        </>
      )}
      {(state.status === "missing" || state.status === "error") && (
        <>
          <XCircle size={56} className="mx-auto text-red-600" />
          <h1 className="font-display text-3xl mt-6">{T("Session introuvable")}</h1>
        </>
      )}
      <Link to="/dropship" data-testid="back-dropship" className="mt-8 inline-flex items-center gap-2 text-sm papicoo-link-underline">
        <ArrowLeft size={14} /> {T("Retour au catalogue")}
      </Link>
    </div>
  );
}
